import { AppLayout } from '@/components/AppLayout';
import { getCurrencySymbol } from '@/lib/currency';
import { Card } from '@/components/ui/card';
import { StageBadge } from '@/components/StageBadge';
import { useJobs, useUpdateJob, type Job } from '@/hooks/useJobs';
import { useDashboardStats } from '@/hooks/useDashboard';
import { useJobViews } from '@/hooks/useJobViews';
import { useJobApplicationCountsBulk } from '@/hooks/useJobApplicationCountsBulk';
import { CreateJobDialog } from '@/components/CreateJobDialog';
import { EditJobDialog } from '@/components/EditJobDialog';
import { AssignJobRecruiterSelect } from '@/components/AssignJobRecruiterSelect';
import { Search, MapPin, Clock, Play, Pause, Share2, XCircle, History, RotateCcw, Briefcase, LayoutGrid, LayoutList, ArrowRight, Users, Eye, AlertTriangle, Timer, CalendarPlus, Crown, UserCheck, UserPlus, ShieldOff } from 'lucide-react';
import { useMyJobAssignments } from '@/hooks/useJobAssignments';
import { useGovernedAction } from '@/hooks/useGovernedAction';
import { SERVICE_KEYS } from '@/lib/serviceKeys';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Link } from 'react-router-dom';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { useState, useEffect, useCallback } from 'react';
import { Skeleton } from '@/components/ui/skeleton';
import { differenceInDays } from 'date-fns';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useTranslation } from 'react-i18next';
import { detectTextDirection } from '@/lib/textDirection';
import { formatLocalizedDate } from '@/lib/localizedDate';
import { useDirection } from '@/hooks/useDirection';
import { HIRE_LIST_HEADER_FLOW, HIRE_PAGE_CONTAINER, HIRE_SECTION_SPACING, HIRE_STAT_GRID, HIRE_STAT_PAD, HIRE_PAGE_HEADER_PAD, HIRE_CARD_GRID } from '@/lib/hireResponsive';
import { formatJobId } from '@/lib/jobId';
import { RESPONSIVE_ACTION_ROW, RESPONSIVE_HEADER_ROW, SAFE_COMPACT_VALUE, SAFE_TEXT_WRAP } from '@/lib/responsiveText';
import { groupJobsByStatus } from '@/lib/jobStatusSemantics';
import { buildPublicJobApplicationUrl } from '@/lib/selfHostedUrls';
import {
  isActiveJobQuotaCapacityBlock,
  shouldBlockJobCapacityReducingActions,
} from '@/lib/forkhire/jobLifecycleGovernance';

import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

type ViewMode = 'list' | 'grid';

export default function Jobs() {
  const { t, i18n } = useTranslation();
  const language = i18n.resolvedLanguage ?? i18n.language;
  const { dir } = useDirection();
  const [search, setSearch] = useState('');
  const [viewMode, setViewMode] = useState<ViewMode>('list');
  const [visibleActive, setVisibleActive] = useState(6);
  const [visibleDraft, setVisibleDraft] = useState(6);
  const [visibleClosed, setVisibleClosed] = useState(6);
  const { data: jobs, isLoading } = useJobs();
  const { data: stats } = useDashboardStats();
  const updateJob = useUpdateJob();
  const { toast } = useToast();
  const { role, session } = useAuth();
  const isAdmin = role === 'org_owner' || role === 'org_admin';
  const jobGov = useGovernedAction(SERVICE_KEYS.JOB_MANAGEMENT);
  const activeJobQuotaCapacityBlock = isActiveJobQuotaCapacityBlock(jobGov);
  const jobCapacityReducingActionsBlocked = shouldBlockJobCapacityReducingActions(jobGov);
  const jobIds = (jobs || []).map(j => j.id);
  const { data: viewCounts } = useJobViews(jobIds);
  const { data: appCounts } = useJobApplicationCountsBulk(jobIds);
  const { data: myAssignments } = useMyJobAssignments();

  // Build a map: jobId -> assignment_role for the current user
  const assignmentMap = (myAssignments || []).reduce<Record<string, string>>((acc, a) => {
    acc[a.job_id] = a.assignment_role;
    return acc;
  }, {});

  const getAssignmentBadge = (job: Job) => {
    const assignRole = assignmentMap[job.id];
    if (assignRole === 'owner') {
      return (
        <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-yellow-400/20 text-yellow-500">
          <Crown className="w-3 h-3" />
        </span>
      );
    }
    if (assignRole === 'collaborator') {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase bg-primary/10 text-primary">
          <UserCheck className="w-3 h-3" /> {t('hire.jobs.assigned')}
        </span>
      );
    }
    // If not admin/recruiter and not in job_assignments, they can only see this via candidate assignment
    if (!isAdmin && role !== 'recruiter' && job.created_by !== session?.user?.id) {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase bg-muted text-muted-foreground">
          <Users className="w-3 h-3" /> {t('hire.jobs.viaCandidate')}
        </span>
      );
    }
    return null;
  };

  /** Whether the current user can manage (status change, close, extend) a specific job */
  const canManageJob = (job: Job) => {
    if (isAdmin || role === 'recruiter') return true;
    if (assignmentMap[job.id] === 'owner') return true;
    if (job.created_by === session?.user?.id) return true;
    return false;
  };

  // Reset visible counts when search changes
  useEffect(() => {
    setVisibleActive(6);
    setVisibleDraft(6);
    setVisibleClosed(6);
  }, [search]);

  const [reopenJob, setReopenJob] = useState<Job | null>(null);
  const [reopenDays, setReopenDays] = useState('30');

  // Extend dialog state
  const [extendJob, setExtendJob] = useState<Job | null>(null);
  const [extendDays, setExtendDays] = useState('15');

  // Confirmation dialog state
  const [confirmAction, setConfirmAction] = useState<{ job: Job; action: 'deactivate' | 'activate' | 'close' } | null>(null);

  useEffect(() => {
    supabase.rpc('close_expired_jobs').then();
  }, []);

  const groupedJobs = groupJobsByStatus(jobs || []);
  const activeJobs = groupedJobs.active;
  const draftJobs = groupedJobs.draft;
  const closedJobs = groupedJobs.history;

  const filterList = useCallback((list: Job[]) =>
    list.filter(j => {
      const q = search.toLowerCase();
      const jobIdStr = j.job_number ? `job-${String(j.job_number).padStart(4, '0')}` : '';
      return j.title.toLowerCase().includes(q) ||
        (j.department || '').toLowerCase().includes(q) ||
        jobIdStr.includes(q) ||
        String(j.job_number || '').includes(q);
    }), [search]);

  const guardJobLifecycleAction = <TArgs extends unknown[]>(
    action: (...args: TArgs) => void,
    allowAtActiveJobCapacity = false,
  ) =>
    (...args: TArgs) => {
      if (jobGov.shouldDisable && !(allowAtActiveJobCapacity && activeJobQuotaCapacityBlock)) {
        toast({
          title: 'Action Blocked',
          description: jobGov.message || 'This service is currently unavailable.',
          variant: 'destructive',
        });
        return;
      }
      action(...args);
    };

  const handleToggleStatus = (e: React.MouseEvent, job: Job) => guardJobLifecycleAction(() => {
    e.preventDefault();
    e.stopPropagation();
    const action = job.status === 'active' ? 'deactivate' : 'activate';
    setConfirmAction({ job, action });
  }, job.status === 'active')();

  const handleCloseJob = guardJobLifecycleAction((e: React.MouseEvent, job: Job) => {
    e.preventDefault();
    e.stopPropagation();
    setConfirmAction({ job, action: 'close' });
  }, true);

  const handleConfirmAction = () => {
    if (!confirmAction) return;
    const { job, action } = confirmAction;
    if (action === 'deactivate') {
      updateJob.mutate({ id: job.id, status: 'inactive' }, {
        onSuccess: () => {
          toast({ title: t('hire.jobs.jobDeactivated'), description: t('hire.jobs.jobDeactivatedDesc', { title: job.title }) });
          setConfirmAction(null);
        },
      });
    } else if (action === 'activate') {
      updateJob.mutate({ id: job.id, status: 'active' }, {
        onSuccess: () => {
          toast({ title: t('hire.jobs.jobActivated'), description: t('hire.jobs.jobActivatedDesc', { title: job.title }) });
          setConfirmAction(null);
        },
      });
    } else if (action === 'close') {
      updateJob.mutate({ id: job.id, status: 'closed', closed_reason: 'manual_close' }, {
        onSuccess: () => {
          toast({ title: t('hire.jobs.jobClosed'), description: t('hire.jobs.jobClosedDesc', { title: job.title }) });
          setConfirmAction(null);
        },
      });
    }
  };

  const handleShareLink = (e: React.MouseEvent, job: Job) => {
    e.preventDefault();
    e.stopPropagation();
    if (job.public_slug) {
      const url = buildPublicJobApplicationUrl(job.public_slug);
      navigator.clipboard.writeText(url);
      toast({ title: t('hire.jobs.linkCopied'), description: t('hire.jobs.linkCopiedDesc') });
    } else {
      toast({ title: t('hire.jobs.noPublicLink'), description: t('hire.jobs.noPublicLinkDesc'), variant: 'destructive' });
    }
  };

  const handleOpenReopenDialog = guardJobLifecycleAction((e: React.MouseEvent, job: Job) => {
    e.preventDefault();
    e.stopPropagation();
    setReopenJob(job);
    setReopenDays('30');
  });

  const handleConfirmReopen = () => {
    if (!reopenJob) return;
    const days = Number(reopenDays);
    if (!days) {
      toast({ title: t('hire.jobs.invalidDuration'), description: t('hire.jobs.invalidDurationDesc'), variant: 'destructive' });
      return;
    }
    updateJob.mutate(
      { id: reopenJob.id, status: 'active', open_duration_days: days, reopen_count: (reopenJob.reopen_count || 0) + 1, reopened_at: new Date().toISOString(), closed_reason: null },
      {
        onSuccess: () => {
          toast({ title: t('hire.jobs.jobReopened'), description: t('hire.jobs.jobReopenedDesc', { title: reopenJob.title }) });
          setReopenJob(null);
        },
      }
    );
  };

  const handleOpenExtendDialog = guardJobLifecycleAction((e: React.MouseEvent, job: Job) => {
    e.preventDefault();
    e.stopPropagation();
    setExtendJob(job);
    setExtendDays('15');
  });

  const handleConfirmExtend = () => {
    if (!extendJob) return;
    const days = Number(extendDays);
    if (!days) return;

    // Always set expiry to now + selected days (7/15/30), never cumulatively add
    const baseDate = new Date();
    const newClosesAt = new Date(baseDate.getTime() + days * 24 * 60 * 60 * 1000).toISOString();
    const updates: Partial<Job> & { id: string } = { id: extendJob.id, closes_at: newClosesAt };
    if (extendJob.status === 'expired') {
      updates.status = 'active';
      updates.closed_reason = null;
    }
    updateJob.mutate(updates, {
      onSuccess: () => {
        toast({ title: t('hire.jobs.jobExtended'), description: t('hire.jobs.jobExtendedDesc', { title: extendJob.title, count: days }) });
        setExtendJob(null);
      },
    });
  };

  const getDaysRemaining = (job: Job) => {
    if (!job.closes_at || job.status !== 'active') return null;
    return differenceInDays(new Date(job.closes_at), new Date());
  };

  const renderJobCard = (job: Job, showActions = true) => {
    const daysLeft = getDaysRemaining(job);
    const isExpiringSoon = daysLeft !== null && daysLeft <= 7 && daysLeft > 3;
    const isExpiringUrgent = daysLeft !== null && daysLeft <= 3 && daysLeft >= 0;
    const canReopen = ['closed', 'expired'].includes(job.status) && (job.reopen_count || 0) < 1;
    const canExtend = ['active', 'expired'].includes(job.status);

    return (
      <Link key={job.id} to={`/jobs/${job.id}`} className="block">
        <div className="flex flex-col sm:flex-row sm:items-center gap-3 px-5 py-4 hover:bg-muted/40 transition-colors group border-b border-border last:border-b-0">
          {/* Left: Icon + Job Info */}
          <div className="flex items-start gap-3 flex-1 min-w-0">
            <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0 mt-0.5">
              <Briefcase className="w-4 h-4 text-primary" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-[10px] font-mono font-bold text-muted-foreground bg-muted px-1.5 py-0.5 rounded">{formatJobId(job.job_number)}</span>
                <p className="font-semibold text-sm text-foreground" dir={detectTextDirection(job.title)}>{job.title}</p>
                <StageBadge stage={job.status} />
                {getAssignmentBadge(job)}
                {isExpiringUrgent && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase bg-destructive/10 text-destructive">
                    <AlertTriangle className="w-3 h-3" /> {t('hire.jobs.expiring')}
                  </span>
                )}
                {isExpiringSoon && !isExpiringUrgent && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase bg-warning/10 text-warning">
                    <Timer className="w-3 h-3" /> {t('hire.jobs.closingSoon')}
                  </span>
                )}
              </div>
              {/* Metadata row — consistent spacing */}
              <div className="flex items-center gap-x-4 gap-y-1 text-xs text-muted-foreground mt-1.5 flex-wrap">
                {job.location && (
                  <span className="inline-flex items-center gap-1">
                    <MapPin className="w-3 h-3 flex-shrink-0" /> {job.location}
                  </span>
                )}
                {job.department && (
                  <span className="inline-flex items-center gap-1 text-muted-foreground">{job.department}</span>
                )}
                {job.employment_type && (
                  <span className="capitalize">{job.employment_type}</span>
                )}
                <span className="inline-flex items-center gap-1">
                  <Clock className="w-3 h-3 flex-shrink-0" /> {formatLocalizedDate(job.created_at, 'PP', language)}
                </span>
                {job.closes_at && (
                  <span>{t('hire.jobs.expiresDate', { date: formatLocalizedDate(job.closes_at, 'MMM d', language) })}</span>
                )}
                {daysLeft !== null && daysLeft >= 0 && (
                  <span className={`font-medium ${isExpiringUrgent ? 'text-destructive' : isExpiringSoon ? 'text-warning' : 'text-muted-foreground'}`}>
                    {t('hire.jobs.daysLeft', { count: daysLeft })}
                  </span>
                )}
              </div>
              {/* Views + Assign */}
              <div className="flex items-center gap-2 mt-1.5" onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}>
                <span className="inline-flex items-center gap-1 text-xs font-medium text-primary bg-primary/10 px-2 py-0.5 rounded-full">
                  <Eye className="w-3 h-3" /> {viewCounts?.[job.id]?.total ?? 0}
                </span>
                <span className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full ${(appCounts?.[job.id] ?? 0) === 0 ? 'text-destructive bg-destructive/10' : 'text-success bg-success/15'}`}>
                  <Users className="w-3 h-3" /> {appCounts?.[job.id] ?? 0}
                </span>
                <AssignJobRecruiterSelect jobId={job.id} assignedTo={job.default_assignee} />
              </div>
            </div>
          </div>

          {/* Right: Actions — always aligned right */}
          <div className="flex max-w-full flex-wrap items-center gap-1.5 sm:ms-auto" onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}>
            {showActions && canManageJob(job) && !['closed', 'expired'].includes(job.status) && (
              <>
                <Button
                  variant="outline"
                  size="sm"
                  className={`text-xs h-8 rounded-xl ${job.status === 'active' ? 'border-warning text-warning hover:bg-warning/10' : 'border-success text-success hover:bg-success/10'}`}
                  onClick={(e) => handleToggleStatus(e, job)}
                  disabled={updateJob.isPending || (job.status === 'active' ? jobCapacityReducingActionsBlocked : jobGov.shouldDisable)}
                >
                  {job.status === 'active' ? <><Pause className="w-3.5 h-3.5 me-1" /> {t('common.deactivate')}</> : <><Play className="w-3.5 h-3.5 me-1" /> {t('common.activate')}</>}
                </Button>
                {canExtend && (
                  <Button variant="outline" size="sm" className="text-xs h-8 rounded-xl border-primary/40 text-primary hover:bg-primary/10" onClick={(e) => handleOpenExtendDialog(e, job)} disabled={updateJob.isPending || jobGov.shouldDisable}>
                    <CalendarPlus className="w-3.5 h-3.5 me-1" /> {t('hire.jobs.extend')}
                  </Button>
                )}
                <Button variant="outline" size="sm" className="text-xs h-8 rounded-xl" onClick={(e) => handleShareLink(e, job)}>
                  <Share2 className="w-3.5 h-3.5 me-1" /> {t('common.share')}
                </Button>
                
                <Button
                  variant="outline"
                  size="sm"
                  className="text-xs border-destructive/40 text-destructive hover:bg-destructive/10 h-8 rounded-xl"
                  onClick={(e) => handleCloseJob(e, job)}
                  disabled={updateJob.isPending || jobCapacityReducingActionsBlocked}
                >
                  <XCircle className="w-3.5 h-3.5 me-1" /> {t('hire.jobs.closeJob')}
                </Button>
              </>
            )}
            {canManageJob(job) && ['closed', 'expired'].includes(job.status) && (
              <>
                {canExtend && (
                  <Button variant="outline" size="sm" className="text-xs h-8 rounded-xl border-primary/40 text-primary hover:bg-primary/10" onClick={(e) => handleOpenExtendDialog(e, job)} disabled={updateJob.isPending || jobGov.shouldDisable}>
                    <CalendarPlus className="w-3.5 h-3.5 me-1" /> {t('hire.jobs.extend')}
                  </Button>
                )}
                {canReopen && isAdmin && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="text-xs border-success text-success hover:bg-success/10 h-8 rounded-xl"
                    onClick={(e) => handleOpenReopenDialog(e, job)}
                    disabled={updateJob.isPending || jobGov.shouldDisable}
                  >
                    <RotateCcw className="w-3.5 h-3.5 me-1" /> {t('common.reopen')}
                  </Button>
                )}
                {!canReopen && ['closed', 'expired'].includes(job.status) && (job.reopen_count || 0) >= 1 && (
                  <span className="text-[10px] text-muted-foreground italic">{t('hire.jobs.reopenLimitReached')}</span>
                )}
              </>
            )}
            <ArrowRight className="w-4 h-4 text-muted-foreground/0 group-hover:text-muted-foreground transition-colors ms-1 rtl-flip" />
          </div>
        </div>
      </Link>
    );
  };

  const renderGridCard = (job: Job) => {
    const daysLeft = getDaysRemaining(job);
    const isExpiringSoon = daysLeft !== null && daysLeft <= 7 && daysLeft > 3;
    const isExpiringUrgent = daysLeft !== null && daysLeft <= 3 && daysLeft >= 0;

    return (
      <Link key={job.id} to={`/jobs/${job.id}`}>
        <Card className="shadow-card hover:shadow-card-hover transition-all border-border hover:border-primary/30 cursor-pointer rounded-2xl press-scale hover-lift h-full flex flex-col overflow-hidden group relative">
          {/* Gradient accent top bar */}
          <div className="h-1.5 w-full bg-primary rounded-t-2xl" />

          {/* Header */}
          <div className="px-5 pt-4 pb-0">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2.5">
                <div className="w-11 h-11 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0 shadow-sm">
                  <Briefcase className="w-5 h-5 text-primary" />
                </div>
                <StageBadge stage={job.status} />
                {getAssignmentBadge(job)}
              </div>
              {isExpiringUrgent && (
                <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-bold uppercase bg-destructive/10 text-destructive animate-pulse">
                  <AlertTriangle className="w-3 h-3" /> {t('hire.jobs.expiring')}
                </span>
              )}
              {isExpiringSoon && !isExpiringUrgent && (
                <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-bold uppercase bg-warning/10 text-warning">
                  <Timer className="w-3 h-3" /> {t('hire.jobs.closingSoon')}
                </span>
              )}
            </div>

            {/* Title */}
            <span className="text-[10px] font-mono font-bold text-muted-foreground bg-muted px-1.5 py-0.5 rounded inline-block mb-1.5">{formatJobId(job.job_number)}</span>
            <h3 className="font-display font-bold text-base text-foreground mb-3 line-clamp-2 leading-snug group-hover:text-primary transition-colors" dir={detectTextDirection(job.title)}>{job.title}</h3>

            {/* Metadata */}
            <div className="space-y-2 text-[13px] text-muted-foreground flex-1 mb-1">
              {job.location && (
                <span className="flex items-center gap-2">
                  <MapPin className="w-3.5 h-3.5 flex-shrink-0 text-primary/50" /> {job.location}
                </span>
              )}
              {(job.department || job.employment_type) && (
                <div className="flex items-center gap-2 flex-wrap">
                  {job.department && (
                    <span className="text-muted-foreground font-medium">{job.department}</span>
                  )}
                  {job.employment_type && (
                    <span className="capitalize px-2 py-0.5 rounded-md bg-secondary text-secondary-foreground text-[11px] font-medium border border-border">{job.employment_type}</span>
                  )}
                </div>
              )}
              {(job.salary_min || job.salary_max) && (
                <span className="flex items-center gap-1 font-semibold text-foreground">
                  {job.salary_min && job.salary_max
                    ? <>{job.salary_min.toLocaleString()} – {job.salary_max.toLocaleString()} {getCurrencySymbol(job.salary_currency || 'SAR')}</>
                    : job.salary_min
                      ? <>{t('hire.jobs.salaryFrom', { amount: job.salary_min.toLocaleString(), currency: getCurrencySymbol(job.salary_currency || 'SAR') })}</>
                      : <>{t('hire.jobs.salaryUpTo', { amount: (job.salary_max as number).toLocaleString(), currency: getCurrencySymbol(job.salary_currency || 'SAR') })}</>}
                </span>
              )}
              {daysLeft !== null && daysLeft >= 0 && (
                <div className="flex items-center gap-1.5">
                  <div className="flex-1 h-1.5 rounded-full bg-muted overflow-hidden">
                    <div
                      className={`h-full rounded-full transition-all ${isExpiringUrgent ? 'bg-destructive' : isExpiringSoon ? 'bg-warning' : 'bg-primary/40'}`}
                      style={{ width: `${Math.max(5, Math.min(100, (daysLeft / 30) * 100))}%` }}
                    />
                  </div>
                  <span className={`text-[11px] font-medium whitespace-nowrap ${isExpiringUrgent ? 'text-destructive' : isExpiringSoon ? 'text-warning' : 'text-muted-foreground'}`}>
                    {t('hire.jobs.daysLeft', { count: daysLeft })}
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Footer */}
          <div className="mt-auto px-5 pb-4 pt-2">
            <div className="flex items-center justify-between pt-3 border-t border-border/60 text-xs text-muted-foreground">
              <span className="flex items-center gap-1.5">
                <Clock className="w-3.5 h-3.5 text-muted-foreground/60" /> {formatLocalizedDate(job.created_at, 'MMM d', language)}
                {job.closes_at && (
                  <span className="text-muted-foreground/50">· {t('hire.jobs.expiresDate', { date: formatLocalizedDate(job.closes_at, 'MMM d', language) })}</span>
                )}
              </span>
              <div className="flex items-center gap-1.5">
                <span className="inline-flex items-center gap-1 font-semibold text-primary bg-primary/10 px-2.5 py-1 rounded-full text-[11px]">
                  <Eye className="w-3 h-3" /> {viewCounts?.[job.id]?.total ?? 0}
                </span>
                <span className={`inline-flex items-center gap-1 font-semibold px-2.5 py-1 rounded-full text-[11px] ${(appCounts?.[job.id] ?? 0) === 0 ? 'text-destructive bg-destructive/10' : 'text-success bg-success/15'}`}>
                  <Users className="w-3 h-3" /> {appCounts?.[job.id] ?? 0}
                </span>
              </div>
            </div>
          </div>

          {/* Hover arrow indicator */}
          <div className="absolute top-4 end-4 opacity-0 group-hover:opacity-100 transition-opacity">
            <ArrowRight className="w-4 h-4 text-primary rtl-flip" />
          </div>
        </Card>
      </Link>
    );
  };

  return (
    <AppLayout>
      <div className={`${HIRE_SECTION_SPACING} ${HIRE_PAGE_CONTAINER}`}>
        {/* Header Card */}
        <Tabs defaultValue="active" dir={dir} className="space-y-6">

        {/* Governance alert */}
        {jobGov.shouldDisable && !activeJobQuotaCapacityBlock && (
          <Alert variant="destructive" className="border-destructive/30 bg-destructive/5">
            <ShieldOff className="h-4 w-4" />
            <AlertDescription className="text-sm">
              {jobGov.message || t('hire.jobs.managementDisabled')}
            </AlertDescription>
          </Alert>
        )}

        <div className={HIRE_LIST_HEADER_FLOW}>
<Card className="shadow-card border-border border-t-4 border-t-primary overflow-hidden">
            <div className={`${HIRE_PAGE_HEADER_PAD} ${RESPONSIVE_HEADER_ROW}`}>
              <div className="min-w-0 max-w-3xl">
                <h1 className={`text-xl sm:text-2xl lg:text-3xl font-bold font-display text-foreground ${SAFE_TEXT_WRAP}`}>
                  {t('hire.jobs.title')}
                </h1>
                <p className={`text-muted-foreground mt-1.5 text-sm ${SAFE_TEXT_WRAP}`}>
                  {t('hire.jobs.totalPositions', { count: jobs?.length || 0 })}
                </p>
              </div>
              <div className={RESPONSIVE_ACTION_ROW}>
                <CreateJobDialog />
              </div>
            </div>

            {/* Stats row inside header */}
            <div className="grid grid-cols-1 gap-4 px-6 pb-6 sm:grid-cols-2 sm:px-8 sm:pb-8 xl:grid-cols-4">
              <div className="bg-muted/40 border border-border rounded-2xl p-4 sm:p-5 relative">
                <div className="flex items-start gap-3">
                  <div className="w-11 h-11 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <Briefcase className="w-5 h-5 text-primary" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-2xl lg:text-3xl font-bold font-display text-foreground tabular-nums">
                      {isLoading ? <Skeleton className="h-8 w-12" /> : (stats?.activeJobs ?? 0).toLocaleString()}
                    </p>
                    <p className={`text-xs sm:text-sm text-muted-foreground font-medium mt-1 ${SAFE_COMPACT_VALUE}`}>{t('hire.jobs.activeJobs')}</p>
                  </div>
                </div>
              </div>
              <div className="bg-muted/40 border border-border rounded-2xl p-4 sm:p-5 relative">
                <div className="flex items-start gap-3">
                  <div className="w-11 h-11 rounded-xl bg-destructive/10 flex items-center justify-center flex-shrink-0">
                    <AlertTriangle className="w-5 h-5 text-destructive" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-2xl lg:text-3xl font-bold font-display text-foreground tabular-nums">
                      {isLoading ? <Skeleton className="h-8 w-12" /> : (stats?.zeroApplicationJobs ?? 0)}
                    </p>
                    <p className={`text-xs sm:text-sm text-muted-foreground font-medium mt-1 ${SAFE_COMPACT_VALUE}`}>{t('hire.jobs.zeroAppJobs')}</p>
                  </div>
                </div>
              </div>
              <div className="bg-muted/40 border border-border rounded-2xl p-4 sm:p-5 relative">
                <div className="flex items-start gap-3">
                  <div className="w-11 h-11 rounded-xl bg-warning/10 flex items-center justify-center flex-shrink-0">
                    <CalendarPlus className="w-5 h-5 text-warning" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-2xl lg:text-3xl font-bold font-display text-foreground tabular-nums">
                      {isLoading ? <Skeleton className="h-8 w-12" /> : (stats?.avgDaysToHire ?? 0)}
                    </p>
                    <p className={`text-xs sm:text-sm text-muted-foreground font-medium mt-1 ${SAFE_COMPACT_VALUE}`}>{t('hire.jobs.daysToHire')}</p>
                  </div>
                </div>
              </div>
              <div className="bg-muted/40 border border-border rounded-2xl p-4 sm:p-5 relative">
                <div className="flex items-start gap-3">
                  <div className="w-11 h-11 rounded-xl bg-warning/10 flex items-center justify-center flex-shrink-0">
                    <Timer className="w-5 h-5 text-warning" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-2xl lg:text-3xl font-bold font-display text-foreground tabular-nums">
                      {isLoading ? <Skeleton className="h-8 w-12" /> : (stats?.closingSoon ?? 0)}
                    </p>
                    <p className={`text-xs sm:text-sm text-muted-foreground font-medium mt-1 ${SAFE_COMPACT_VALUE}`}>{t('hire.jobs.closingSoon')}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Tabs + Search + View Toggle */}
            <div className="flex flex-col xl:flex-row xl:items-center gap-3 px-6 sm:px-8 py-4 border-t border-border">
              <div className="relative flex-1 min-w-0">
                <Search className="absolute start-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder={t('hire.jobs.searchPlaceholder')}
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="ps-9 h-10 rounded-xl"
                />
              </div>
              <div className="flex flex-col xs:flex-row flex-wrap gap-2 justify-between xl:justify-end">
                <TabsList className="rounded-xl h-auto flex-wrap">
                  <TabsTrigger value="active" className="rounded-lg touch-target">{t('hire.jobs.active')} ({activeJobs.length})</TabsTrigger>
                  <TabsTrigger value="draft" className="rounded-lg touch-target">{t('hire.jobs.drafts')} ({draftJobs.length})</TabsTrigger>
                  <TabsTrigger value="history" className="gap-1.5 rounded-lg touch-target">
                    <History className="w-4 h-4" /> {t('hire.jobs.inactiveClosed')} ({closedJobs.length})
                  </TabsTrigger>
                </TabsList>
                <div className="flex items-center gap-1 border border-border rounded-xl p-1 bg-muted/30">
                  <Button
                    variant={viewMode === 'list' ? 'secondary' : 'ghost'}
                    size="sm"
                    className="h-8 w-8 p-0 rounded-lg"
                    onClick={() => setViewMode('list')}
                  >
                    <LayoutList className="w-4 h-4" />
                  </Button>
                  <Button
                    variant={viewMode === 'grid' ? 'secondary' : 'ghost'}
                    size="sm"
                    className="h-8 w-8 p-0 rounded-lg"
                    onClick={() => setViewMode('grid')}
                  >
                  <LayoutGrid className="w-4 h-4" />
                </Button>
              </div>
              </div>
            </div>
          </Card>
        </div>

        {/* Job List */}
<Card className={`shadow-card border-border overflow-hidden ${viewMode === 'list' ? 'border-s-4 border-s-primary' : ''}`}>
            <TabsContent value="active" className="mt-0">
              {isLoading ? (
                <div className="p-4 space-y-3">
                  {[1, 2, 3].map(i => <Skeleton key={i} className="h-16 w-full rounded-xl" />)}
                </div>
              ) : filterList(activeJobs).length === 0 ? (
                <div className="flex flex-col items-center justify-center p-10 text-center">
                  <Briefcase className="w-12 h-12 text-muted-foreground/30 mx-auto mb-3" />
                  <p className="text-sm font-medium text-foreground mb-1">{t('hire.jobs.noActiveJobsTitle')}</p>
                  <p className="text-xs text-muted-foreground mb-4">{t('hire.jobs.noActiveJobs')}</p>
                </div>
              ) : viewMode === 'grid' ? (
                <>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 p-5">
                    {filterList(activeJobs).slice(0, visibleActive).map((job) => renderGridCard(job))}
                  </div>
                  {visibleActive < filterList(activeJobs).length && (
                    <div className="flex justify-center pb-5">
                      <Button variant="outline" className="rounded-xl" onClick={() => setVisibleActive(v => v + 6)}>
                        {t('hire.jobs.showMoreRemaining', { count: filterList(activeJobs).length - visibleActive })}
                      </Button>
                    </div>
                  )}
                </>
              ) : (
                <>
                  <div className="divide-y divide-border">
                    {filterList(activeJobs).slice(0, visibleActive).map((job) => renderJobCard(job, true))}
                  </div>
                  {visibleActive < filterList(activeJobs).length && (
                    <div className="flex justify-center py-5 border-t border-border">
                      <Button variant="outline" className="rounded-xl" onClick={() => setVisibleActive(v => v + 6)}>
                        {t('hire.jobs.showMoreRemaining', { count: filterList(activeJobs).length - visibleActive })}
                      </Button>
                    </div>
                  )}
                </>
              )}
            </TabsContent>

            <TabsContent value="draft" className="mt-0">
              {filterList(draftJobs).length === 0 ? (
                <div className="flex flex-col items-center justify-center p-10 text-center">
                  <Briefcase className="w-12 h-12 text-muted-foreground/30 mx-auto mb-3" />
                  <p className="text-sm font-medium text-foreground mb-1">{t('hire.jobs.noDraftJobsTitle')}</p>
                  <p className="text-xs text-muted-foreground">{t('hire.jobs.noDraftJobs')}</p>
                </div>
              ) : viewMode === 'grid' ? (
                <>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 p-5">
                    {filterList(draftJobs).slice(0, visibleDraft).map((job) => renderGridCard(job))}
                  </div>
                  {visibleDraft < filterList(draftJobs).length && (
                    <div className="flex justify-center pb-5">
                      <Button variant="outline" className="rounded-xl" onClick={() => setVisibleDraft(v => v + 6)}>
                        {t('hire.jobs.showMoreRemaining', { count: filterList(draftJobs).length - visibleDraft })}
                      </Button>
                    </div>
                  )}
                </>
              ) : (
                <>
                  <div className="divide-y divide-border">
                    {filterList(draftJobs).slice(0, visibleDraft).map((job) => renderJobCard(job, true))}
                  </div>
                  {visibleDraft < filterList(draftJobs).length && (
                    <div className="flex justify-center py-5 border-t border-border">
                      <Button variant="outline" className="rounded-xl" onClick={() => setVisibleDraft(v => v + 6)}>
                        {t('hire.jobs.showMoreRemaining', { count: filterList(draftJobs).length - visibleDraft })}
                      </Button>
                    </div>
                  )}
                </>
              )}
            </TabsContent>

            <TabsContent value="history" className="mt-0">
              {filterList(closedJobs).length === 0 ? (
                <div className="flex flex-col items-center justify-center p-10 text-center">
                  <History className="w-12 h-12 text-muted-foreground/30 mx-auto mb-3" />
                  <p className="text-sm text-muted-foreground">{t('hire.jobs.noInactiveClosedJobs')}</p>
                </div>
              ) : viewMode === 'grid' ? (
                <>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 p-5">
                    {filterList(closedJobs).slice(0, visibleClosed).map((job) => renderGridCard(job))}
                  </div>
                  {visibleClosed < filterList(closedJobs).length && (
                    <div className="flex justify-center pb-5">
                      <Button variant="outline" className="rounded-xl" onClick={() => setVisibleClosed(v => v + 6)}>
                        {t('hire.jobs.showMoreRemaining', { count: filterList(closedJobs).length - visibleClosed })}
                      </Button>
                    </div>
                  )}
                </>
              ) : (
                <>
                  <div className="divide-y divide-border">
                    {filterList(closedJobs).slice(0, visibleClosed).map((job) => renderJobCard(job, true))}
                  </div>
                  {visibleClosed < filterList(closedJobs).length && (
                    <div className="flex justify-center py-5 border-t border-border">
                      <Button variant="outline" className="rounded-xl" onClick={() => setVisibleClosed(v => v + 6)}>
                        {t('hire.jobs.showMoreRemaining', { count: filterList(closedJobs).length - visibleClosed })}
                      </Button>
                    </div>
                  )}
                </>
              )}
            </TabsContent>
        </Card>
        </Tabs>
      </div>

      {/* Confirm Action Dialog */}
      <Dialog open={!!confirmAction} onOpenChange={(open) => { if (!open) setConfirmAction(null); }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              {confirmAction?.action === 'deactivate' && t('hire.jobs.deactivateJob')}
              {confirmAction?.action === 'activate' && t('hire.jobs.activateJob')}
              {confirmAction?.action === 'close' && t('hire.jobs.closeJobTitle')}
            </DialogTitle>
            <DialogDescription>
              {confirmAction?.action === 'deactivate' && t('hire.jobs.deactivateConfirm', { title: confirmAction?.job?.title })}
              {confirmAction?.action === 'activate' && t('hire.jobs.activateConfirm', { title: confirmAction?.job?.title })}
              {confirmAction?.action === 'close' && t('hire.jobs.closeConfirm', { title: confirmAction?.job?.title })}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => setConfirmAction(null)}>{t('common.cancel')}</Button>
            <Button
              variant={confirmAction?.action === 'close' ? 'destructive' : 'default'}
              onClick={handleConfirmAction}
              disabled={updateJob.isPending}
            >
              {confirmAction?.action === 'deactivate' && t('common.deactivate')}
              {confirmAction?.action === 'activate' && t('common.activate')}
              {confirmAction?.action === 'close' && t('hire.jobs.closeJobTitle')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!reopenJob} onOpenChange={(open) => { if (!open) setReopenJob(null); }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display">{t('hire.jobs.reopenJob')}</DialogTitle>
            <DialogDescription>
              {t('hire.jobs.reopenDescription', { title: reopenJob?.title })}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label>{t('hire.jobs.openForDays')} <span className="text-destructive">*</span></Label>
              <Select value={reopenDays} onValueChange={setReopenDays}>
                <SelectTrigger className="h-11 mt-1.5"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="14">{t('hire.jobs.days', { count: 14 })}</SelectItem>
                  <SelectItem value="30">{t('hire.jobs.days', { count: 30 })}</SelectItem>
                  <SelectItem value="45">{t('hire.jobs.days', { count: 45 })}</SelectItem>
                  <SelectItem value="60">{t('hire.jobs.days', { count: 60 })}</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground mt-1.5">{t('hire.jobs.durationStartsToday')}</p>
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setReopenJob(null)} className="touch-target">{t('common.cancel')}</Button>
            <Button
              className="border-success text-success hover:bg-success/10 touch-target"
              variant="outline"
              onClick={handleConfirmReopen}
              disabled={updateJob.isPending}
            >
              <RotateCcw className="w-4 h-4 me-1.5" />
              {updateJob.isPending ? t('common.pleaseWait') : t('hire.jobs.reopenJob')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Extend Job Dialog */}
      <Dialog open={!!extendJob} onOpenChange={(open) => { if (!open) setExtendJob(null); }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display">{t('hire.jobs.extendJob')}</DialogTitle>
            <DialogDescription>
              {t('hire.jobs.extendDescription', { title: extendJob?.title })}
              {extendJob?.status === 'expired' && ` ${t('hire.jobs.extendReactivate')}`}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            {extendJob?.closes_at && extendJob?.status === 'active' && (
              <p className="text-sm text-muted-foreground">
                {t('hire.jobs.currentExpiry')} <strong>{formatLocalizedDate(extendJob.closes_at, 'PP', language)}</strong>
              </p>
            )}
            <div>
              <Label>{t('hire.jobs.extendBy')} <span className="text-destructive">*</span></Label>
              <Select value={extendDays} onValueChange={setExtendDays}>
                <SelectTrigger className="h-11 mt-1.5"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="7">{t('hire.jobs.days', { count: 7 })}</SelectItem>
                  <SelectItem value="15">{t('hire.jobs.days', { count: 15 })}</SelectItem>
                  <SelectItem value="30">{t('hire.jobs.days', { count: 30 })}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setExtendJob(null)}>{t('common.cancel')}</Button>
            <Button
              className="border-primary text-primary hover:bg-primary/10"
              variant="outline"
              onClick={handleConfirmExtend}
              disabled={updateJob.isPending}
            >
              <CalendarPlus className="w-4 h-4 me-1.5" />
              {updateJob.isPending ? t('hire.jobs.extending') : t('hire.jobs.extendJob')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AppLayout>
  );
}
